export type OrderSourceTab = "all" | "platform" | "manual";

export type OrderStatus = "Open" | "In Progress" | "Draft";

export type OrderSource = "manual" | "platform";

export type UiOrder = {
  id: number;
  ordercode: string;
  source: OrderSource;
  status: OrderStatus;
  cadMode: "upload" | "auto";
  createdate: string | Date;
  duedate: string | Date | null;
  startdate?: string | Date | null;
  clientid: number | string | null;
  clientName: string;
  partsCount: number;
  /** Azure Blob URL when CAD uploaded (manual orders) */
  cadFileUrl?: string | null;
  /** Original API row when from backend */
  _raw?: Record<string, unknown>;
};

const STATUSES: OrderStatus[] = ["Open", "In Progress", "Draft"];

export function statusFromId(id: number): OrderStatus {
  return STATUSES[Math.abs(id) % 3];
}

/** Partner GET vendor_orders/active_pos — unwrap common envelope shapes */
export function extractPartnerVendorOrdersPayload(body: unknown): {
  rows: unknown[];
  total: number;
} {
  if (body == null) return { rows: [], total: 0 };
  if (Array.isArray(body)) return { rows: body, total: body.length };
  if (typeof body !== "object") return { rows: [], total: 0 };
  const o = body as Record<string, unknown>;

  const rowsCandidate =
    o.data ?? o.orders ?? o.rows ?? o.results ?? o.items ?? o.list ?? o.records;
  if (Array.isArray(rowsCandidate)) {
    const totalNum = Number(
      o.total ??
        o.count ??
        o.totalCount ??
        o.totalRecords ??
        o.recordsTotal ??
        rowsCandidate.length,
    );
    return {
      rows: rowsCandidate,
      total: Number.isFinite(totalNum) ? totalNum : rowsCandidate.length,
    };
  }
  return { rows: [], total: 0 };
}

/** Line-item count like Partner PO detail (parts / PO lines). */
function derivePartnerPOLineCount(row: Record<string, unknown>): number {
  const candidates = [
    row.vendor_order_items,
    row.vendorOrderItems,
    row.purchase_order_lines,
    row.purchaseOrderLines,
    row.line_items,
    row.lineItems,
    row.po_lines,
    row.poLines,
    row.order_lines,
    row.orderLines,
    row.lines,
    row.items,
    row.parts,
  ];
  for (const c of candidates) {
    if (Array.isArray(c)) return c.length;
  }
  const n = Number(
    (row as any).parts_count ??
      (row as any).part_count ??
      (row as any).total_parts ??
      (row as any).line_count ??
      (row as any).total_line_items ??
      (row as any).lines_count ??
      NaN,
  );
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

/**
 * Open vs In Progress: after a MES/job exists for this PO → In Progress; otherwise Open.
 * (Partner list rows may expose job id, nested job, or jobs[].)
 */
function derivePlatformStatusFromJob(row: Record<string, unknown>): OrderStatus {
  const jobObj = row.job;
  const nestedJobId =
    jobObj &&
    typeof jobObj === "object" &&
    ((jobObj as Record<string, unknown>).id ??
      (jobObj as Record<string, unknown>).job_id ??
      (jobObj as Record<string, unknown>).ID);

  const hasJob =
    Boolean(
      row.job_id ??
        row.jobId ??
        row.mes_job_id ??
        row.mesJobId ??
        row.mes_jobId ??
        row.production_job_id ??
        row.productionJobId ??
        nestedJobId,
    ) ||
    (Array.isArray(row.jobs) && row.jobs.length > 0) ||
    row.has_job === true ||
    row.hasJob === true ||
    row.job_created === true ||
    row.is_job_created === true ||
    row.jobCreated === true;

  if (hasJob) return "In Progress";
  return "Open";
}

/** Map one Partner vendor order row → UiOrder (field names vary by API version) */
export function normalizePlatformVendorOrder(row: any): UiOrder {
  const rec = row as Record<string, unknown>;

  const id = Number(
    row.id ??
      row.orderId ??
      row.order_id ??
      row.vendorOrderId ??
      row.vendor_order_id ??
      row.poId ??
      row.po_number_id ??
      row.POId ??
      0,
  );
  const ordercode = String(
    row.orderCode ??
      row.order_code ??
      row.poNumber ??
      row.po_number ??
      row.reference ??
      row.code ??
      (id ? `PO-${id}` : "—"),
  );

  /** Table "Created on" column → Partner `issued_at` */
  const createdate =
    row.issued_at ??
    row.issuedAt ??
    row.issued_date ??
    row.createdAt ??
    row.created_at ??
    row.createdDate ??
    row.created_on ??
    row.createdate ??
    new Date().toISOString();

  /** Table "Due date" column → Partner `dispatch_date` */
  const duedate =
    row.dispatch_date ??
    row.dispatchDate ??
    row.dispatch_at ??
    row.dispatchAt ??
    row.dueDate ??
    row.due_date ??
    row.estimatedDelivery ??
    row.estimated_delivery ??
    null;

  const clientName = String(
    row.company_details ??
      row.companyDetails ??
      row.clientName ??
      row.client_name ??
      row.customerName ??
      row.customer_name ??
      row.vendorName ??
      row.companyName ??
      row.buyerName ??
      row.organizationName ??
      "—",
  );

  const partsCount = derivePartnerPOLineCount(rec);

  const status = derivePlatformStatusFromJob(rec);

  return {
    id: id || Number(row.numericId) || 0,
    ordercode,
    source: "platform",
    status,
    cadMode: "auto",
    createdate,
    duedate,
    clientid: row.clientId ?? row.client_id ?? null,
    clientName,
    partsCount,
    _raw: row,
  };
}

export function normalizeManualOrder(
  row: any,
  clientNameLookup: (clientId: string | number | null | undefined) => string,
): UiOrder {
  const id = Number(row.id);
  const clientId = row.clientid ?? row.clientId ?? row.client_id;
  const clientName = clientNameLookup(clientId);
  const createdate =
    row.createdate ?? row.createddate ?? row.createDate ?? row.created_at ?? new Date().toISOString();
  const duedate = row.duedate ?? row.dueDate ?? row.due_date ?? null;
  const startdate = row.startdate ?? row.startDate ?? row.start_date;
  return {
    id,
    ordercode: row.ordercode || `MO-${id}`,
    source: "manual",
    status: statusFromId(id),
    cadMode: "upload",
    createdate,
    duedate,
    startdate,
    clientid: clientId,
    clientName,
    partsCount: (Math.abs(id) % 5) + 1,
    cadFileUrl:
      (row.cad_file_url as string | undefined) ??
      (row.cadFileUrl as string | undefined) ??
      null,
    _raw: row,
  };
}

/** Demo Truventor Platform rows — replace with API when available */
export const MOCK_PLATFORM_ORDERS: UiOrder[] = [
  {
    id: 900001,
    ordercode: "PO-2024-001",
    source: "platform",
    status: "In Progress",
    cadMode: "auto",
    createdate: "2024-03-18",
    duedate: "2024-04-15",
    clientid: null,
    clientName: "Tata Motors",
    partsCount: 2,
  },
  {
    id: 900002,
    ordercode: "PO-2024-002",
    source: "platform",
    status: "Open",
    cadMode: "auto",
    createdate: "2024-03-20",
    duedate: "2024-04-18",
    clientid: null,
    clientName: "Bajaj Auto",
    partsCount: 1,
  },
  {
    id: 900003,
    ordercode: "PO-2024-003",
    source: "platform",
    status: "Draft",
    cadMode: "auto",
    createdate: "2024-03-22",
    duedate: "2024-04-20",
    clientid: null,
    clientName: "Tata Motors",
    partsCount: 2,
  },
];

export function filterOrdersBySearch(rows: UiOrder[], q: string): UiOrder[] {
  const s = q.trim().toLowerCase();
  if (!s) return rows;
  return rows.filter(
    (r) =>
      r.ordercode?.toLowerCase().includes(s) ||
      r.clientName?.toLowerCase().includes(s),
  );
}

export function sortOrdersByCreatedDesc(rows: UiOrder[]): UiOrder[] {
  return [...rows].sort((a, b) => {
    const ta = new Date(a.createdate as string).getTime();
    const tb = new Date(b.createdate as string).getTime();
    return tb - ta;
  });
}

